// ===========================================
// REALTIME UPDATES HOOK FOR POS
// ===========================================

import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWebSocket, WebSocketMessage } from '@mise/shared';
import { useAuthStore } from '../stores/authStore';
import { useNotificationStore } from '../stores/notificationStore';
import { kitchenApi } from '../services/api';

// WebSocket is optional - only connect if URL is explicitly configured
const WS_URL = import.meta.env.VITE_WS_URL || null;
const WS_ENABLED = !!import.meta.env.VITE_WS_URL;

export function useRealtimeUpdates() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { addNotification } = useNotificationStore();
  const audioContextRef = useRef<AudioContext | null>(null);

  // Play notification sound for course ready
  const playCourseReadySound = useCallback(() => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      // Three ascending tones for "food is ready"
      const playTone = (frequency: number, startTime: number, duration: number) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.25, startTime + 0.02);
        gainNode.gain.linearRampToValueAtTime(0.15, startTime + duration * 0.5);
        gainNode.gain.linearRampToValueAtTime(0, startTime + duration);

        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };

      const now = ctx.currentTime;
      // Three-tone "ding-ding-ding" - food ready notification
      playTone(523.25, now, 0.12);        // C5
      playTone(659.25, now + 0.12, 0.12); // E5
      playTone(783.99, now + 0.24, 0.2);  // G5
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  }, []);

  // Handle WebSocket messages
  const handleMessage = useCallback((message: WebSocketMessage) => {
    switch (message.type) {
      // Order events
      case 'order.created':
      case 'order.updated':
      case 'order.completed':
      case 'order.voided':
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        queryClient.invalidateQueries({ queryKey: ['checks'] });
        queryClient.invalidateQueries({ queryKey: ['check'] });
        break;

      // Table events
      case 'table.status_changed':
      case 'table.cleared':
      case 'table.assigned':
      case 'table.seated':
        queryClient.invalidateQueries({ queryKey: ['tables'] });
        queryClient.invalidateQueries({ queryKey: ['checks'] });
        queryClient.invalidateQueries({ queryKey: ['check'] });
        break;

      // Check events
      case 'check.opened':
      case 'check.updated':
      case 'check.split':
        queryClient.invalidateQueries({ queryKey: ['checks'] });
        queryClient.invalidateQueries({ queryKey: ['check'] });
        break;

      case 'check.transferred':
        queryClient.invalidateQueries({ queryKey: ['checks'] });
        queryClient.invalidateQueries({ queryKey: ['check'] });
        queryClient.invalidateQueries({ queryKey: ['tables'] });
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        break;

      case 'check.closed':
        queryClient.invalidateQueries({ queryKey: ['checks'] });
        queryClient.invalidateQueries({ queryKey: ['check'] });
        queryClient.invalidateQueries({ queryKey: ['tables'] });
        break;

      // Payment events
      case 'payment.processed':
      case 'payment.failed':
      case 'payment.refunded':
        queryClient.invalidateQueries({ queryKey: ['checks'] });
        queryClient.invalidateQueries({ queryKey: ['check'] });
        queryClient.invalidateQueries({ queryKey: ['payments'] });
        break;

      // Kitchen events (for order status)
      case 'kitchen.ticket_ready':
      case 'kitchen.ticket_bumped':
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        break;

      // Course events
      case 'course.fired':
        queryClient.invalidateQueries({ queryKey: ['courses'] });
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        break;

      case 'course.ready':
        // Course is ready - food is up!
        queryClient.invalidateQueries({ queryKey: ['courses'] });
        queryClient.invalidateQueries({ queryKey: ['orders'] });

        // Play notification sound and add to notification store
        playCourseReadySound();
        addNotification({
          type: 'course_ready',
          title: 'Course Ready!',
          message: `Course ${message.payload?.courseNumber || ''} is ready for ${message.payload?.tableNumber ? `Table ${message.payload.tableNumber}` : 'pickup'}`,
          tableNumber: message.payload?.tableNumber,
          courseNumber: message.payload?.courseNumber,
        });
        console.log('[POS] Course ready:', message.payload);
        break;

      case 'course.served':
        queryClient.invalidateQueries({ queryKey: ['courses'] });
        break;

      // Menu/inventory events
      case 'kitchen.item_86d':
      case 'kitchen.item_available':
      case 'inventory.low':
      case 'inventory.depleted':
        queryClient.invalidateQueries({ queryKey: ['menu'] });
        break;

      default:
        // Unknown event type, ignore
        break;
    }
  }, [queryClient, playCourseReadySound, addNotification]);

  // Polling fallback for "food is ready" alerts (no websocket): every 15s ask
  // the server which tickets/courses became ready recently, chime for unseen
  // ones. A failed poll is silently retried next tick — reliable, no
  // connection bar, nothing for staff to think about.
  const seenReadyRef = useRef<Set<string>>(new Set());
  const firstReadyPollRef = useRef(true);
  useEffect(() => {
    if (WS_ENABLED || !token) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await kitchenApi.getReadyAlerts(180);
        if (cancelled || !res.success || !res.data?.alerts) return;
        for (const alert of res.data.alerts) {
          const key = `${alert.orderId}:${alert.courseNumber ?? 'ticket'}`;
          if (seenReadyRef.current.has(key)) continue;
          seenReadyRef.current.add(key);
          // On the very first poll after login, record history without
          // chiming — those dishes were ready before we started watching.
          if (firstReadyPollRef.current) continue;
          playCourseReadySound();
          addNotification({
            type: 'course_ready',
            title: alert.kind === 'course' ? 'Course Ready!' : 'Order Ready!',
            message: `${alert.tableNumber ? `Table ${alert.tableNumber}` : 'An order'}${alert.courseNumber ? ` — course ${alert.courseNumber}` : ''} is ready in the kitchen`,
          });
          queryClient.invalidateQueries({ queryKey: ['orders'] });
        }
        firstReadyPollRef.current = false;
        // Don't let the seen-set grow unbounded over a long service
        if (seenReadyRef.current.size > 500) {
          seenReadyRef.current = new Set(Array.from(seenReadyRef.current).slice(-250));
        }
      } catch {
        // next tick retries
      }
    };

    poll();
    const interval = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [token, queryClient, playCourseReadySound, addNotification]);

  // WebSocket connection - only if explicitly configured
  const {
    isConnected: wsConnected,
    isConnecting,
    error,
    subscribe,
    reconnect,
  } = useWebSocket({
    url: WS_URL || '',
    token: WS_ENABLED ? token : null,  // Pass null to prevent connection
    autoReconnect: WS_ENABLED,
    // Keep retrying quietly forever (30s cap) — the default gives up after
    // 10 attempts, which permanently kills realtime after a server deploy.
    maxReconnectAttempts: Number.POSITIVE_INFINITY,
    onMessage: handleMessage,
    onConnected: () => {
      console.log('[POS] WebSocket connected');
      // Subscribe to relevant channels
      subscribe('orders');
      subscribe('tables');
      subscribe('kitchen');
    },
    onDisconnected: () => {
      if (WS_ENABLED) {
        console.log('[POS] WebSocket disconnected');
      }
    },
  });

  // If WebSocket not configured, consider it "connected" (using polling)
  const isConnected = WS_ENABLED ? wsConnected : true;

  return {
    isConnected,
    isConnecting: WS_ENABLED ? isConnecting : false,
    error: WS_ENABLED ? error : null,
    reconnect,
  };
}
